import { useEffect, useState } from "react";
import axios from "axios";
import { avatarMap } from "../../data/avatarMap";

const AvatarRep = () => {
  const [avatarData, setAvatarData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showTable, setShowTable] = useState(false);
  const [sortBy, setSortBy] = useState("slope");
  const [ownedCounts, setOwnedCounts] = useState(() => {
    // Load cached counts from localStorage
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("avatarOwnedCounts");
      return saved ? JSON.parse(saved) : {};
    }
    return {};
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const avatarResponse = await axios.get("https://api.pixelore.wiki/api/avatars");
        const trustPointsData = avatarResponse.data
          .filter((avatar) => avatar.trustPoints)
          .map((avatar) => ({
            id: avatar.id, // Added for tracking
            customName: avatar.customName || avatar.id,
            minNftsOwned: avatar.trustPoints.minNftsOwned,
            maxPoints: avatar.trustPoints.maxPoints,
            slope: avatar.trustPoints.slope,
          }))
          .sort((a, b) => b[sortBy] - a[sortBy]);

        setAvatarData(trustPointsData);
      } catch (error) {
        console.error("Issue fetching avatar data: ", error);
      }
    };

    fetchData();
  }, [sortBy]);

  // Save to localStorage when ownedCounts changes
  useEffect(() => {
    localStorage.setItem("avatarOwnedCounts", JSON.stringify(ownedCounts));
  }, [ownedCounts]);

  // Calculate reputation for a single avatar
  const calculateReputation = (avatar) => {
    const count = ownedCounts[avatar.id] || 0;
    if (count < avatar.minNftsOwned) return 0;
    return Math.min(avatar.maxPoints, count * avatar.slope);
  };

  // Calculate total reputation across all avatars
  const totalReputation = avatarData.reduce((sum, avatar) => {
    return sum + calculateReputation(avatar);
  }, 0);

  const handleCountChange = (avatarId, value) => {
    const numValue = Math.max(0, parseInt(value) || 0); // Ensure non-negative
    setOwnedCounts((prev) => ({
      ...prev,
      [avatarId]: numValue,
    }));
  };

  const filteredData = avatarData.filter((avatar) => avatar.customName.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="bg-[var(--panel-sections)] p-4 rounded-lg shadow-md mt-4">
      <div className="flex justify-between items-center mb-3">
        <div>
          <div className="text-sm text-[var(--text-color)] mt-1">
            Total Calculated Reputation from Avatars: <span className="font-bold">{totalReputation}</span> points
          </div>
        </div>
        <button
          onClick={() => setShowTable(!showTable)}
          className="panel-button"
        >
          {showTable ? "Hide" : "Show"} Table
        </button>
      </div>

      <p className="text-sm text-[var(--text-color)] mb-2">
        The data below is pulled from official Pixels sources. Enter how many of each avatar you own to calculate your reputation.
      </p>

      {showTable && (
        <>
          <div className="flex gap-3 items-center mb-2">
            <input
              type="text"
              placeholder="Search avatars..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-2 py-1 select-forms"
            />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-2 py-1 select-forms"
            >
              <option value="slope">Sort by Slope</option>
              <option value="maxPoints">Sort by Max Points</option>
              <option value="minNftsOwned">Sort by Min NFTs</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left text-[var(--text-color)] border border-[var(--border)] rounded-lg">
              <thead className="bg-[var(--level-bars-bg)] text-[var(--level-bars)]">
                <tr>
                  <th className="px-3 py-2">Avatar</th>
                  <th className="px-3 py-2">Min NFTs</th>
                  <th className="px-3 py-2">Max Points</th>
                  <th className="px-3 py-2">Slope</th>
                  <th className="px-3 py-2">You Own</th>
                  <th className="px-3 py-2">Points</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((avatar) => (
                  <tr key={avatar.id} className="border-t border-[var(--border)] hover:bg-[var(--hover-bg)]">
                    <td className="px-3 py-2">{avatarMap.find((a) => a.id === avatar.id)?.customName || avatar.customName || avatar.id}</td>
                    <td className="px-3 py-2">{avatar.minNftsOwned}</td>
                    <td className="px-3 py-2">{avatar.maxPoints}</td>
                    <td className="px-3 py-2">{avatar.slope}</td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        value={ownedCounts[avatar.id] || 0}
                        onChange={(e) => handleCountChange(avatar.id, e.target.value)}
                        className="w-16 px-1 py-1 rounded bg-[var(--bg-input)] border border-[var(--border)]"
                      />
                    </td>
                    <td className="px-3 py-2">{calculateReputation(avatar)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AvatarRep;
